import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorText,
  useDeleteRateCheckMutation,
  useListObjectTypesQuery,
  useListRateChecksQuery,
  useListRimRegionsQuery,
  useUploadRateCheckMutation,
} from '@shared/api';
import type { RateCheck } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { ParseWarningsBanner } from './ParseWarningsBanner';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const UploadCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const UploadLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  flex: 1;
  min-width: 280px;
`;

const UploadTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const UploadHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HiddenInput = styled.input`
  display: none;
`;

const RegionSelect = styled.select`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  min-width: 220px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
  }

  td a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
  }

  td a:hover {
    text-decoration: underline;
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ErrorNote = styled(Note)`
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

function badgeToneFor(status: RateCheck['status']): 'warning' | 'success' | 'danger' {
  if (status === 'pending') return 'warning';
  if (status === 'parsed') return 'success';
  return 'danger';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RateChecks() {
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const { data, isLoading, isError } = useListRateChecksQuery();
  const { data: regionsData } = useListRimRegionsQuery();
  const { data: objectTypesData } = useListObjectTypesQuery();
  const [upload, { isLoading: uploading }] = useUploadRateCheckMutation();
  const [deleteCheck] = useDeleteRateCheckMutation();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);
  const [region, setRegion] = useState<string>('');
  const [objectType, setObjectType] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const regions = regionsData?.regions ?? [];
  const objectTypes = objectTypesData?.object_types ?? [];

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadWarnings([]);
    const form = new FormData();
    form.append('file', file);
    // Регион — для приведения цен ФССЦ/ГЭСН (РИМ); тип объекта — для БИМ-индекса
    // ФЕР из письма Минстроя. Не выбраны → соответствующее приведение off.
    if (region) form.append('region', region);
    if (objectType) form.append('object_type', objectType);
    try {
      const res = await upload(form).unwrap();
      setUploadWarnings(res.warnings ?? []);
    } catch (err) {
      setUploadError(apiErrorText(err, 'Не удалось разобрать файл.'));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function onDelete(id: string, filename: string) {
    if (!window.confirm(`Удалить загрузку «${filename}»? Все позиции будут удалены каскадно.`)) {
      return;
    }
    try {
      await deleteCheck(id).unwrap();
    } catch (err) {
      window.alert(apiErrorText(err, 'Не удалось удалить загрузку.'));
    }
  }

  const items = data?.items ?? [];

  return (
    <Wrap>
      <UploadCard $accent>
        <UploadLabel>
          <UploadTitle>Загрузить смету: Excel Гранд-Сметы или XML ГГЭ</UploadTitle>
          <UploadHint>
            Один xlsx-файл с N локальных смет (по листам) или смета в XML ГГЭ (.gge — «Экспорт в XML
            ГГЭ» Гранд-Сметы). Из файла берутся шифры расценок, цены ПЗ/ОЗП/ЭМ/ЗПМ/материалы и
            заявленные НР/СП %; из XML — ещё применённые поправочные коэффициенты и ресурсы позиций:
            по ГЭСН они сверяются с привязкой ГГЭ и технологическими группами. Регион — для
            приведения цен ФССЦ/ГЭСН к текущему уровню по индексам ФГИС ЦС.
          </UploadHint>
        </UploadLabel>
        <RegionSelect
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          disabled={!can_write || uploading}
          aria-label="Регион сметы"
        >
          <option value="">Регион не выбран</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </RegionSelect>
        <RegionSelect
          value={objectType}
          onChange={(e) => setObjectType(e.target.value)}
          disabled={!can_write || uploading}
          aria-label="Тип объекта строительства"
        >
          <option value="">Тип объекта не выбран</option>
          {objectTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </RegionSelect>
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={!can_write || uploading}
        >
          {uploading ? 'Загружаю и парсю…' : 'Выбрать файл'}
        </Button>
        <HiddenInput
          ref={fileRef}
          type="file"
          accept=".xlsx,.gge,.xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={onUpload}
        />
      </UploadCard>

      {uploadError && <ErrorNote>{uploadError}</ErrorNote>}
      <ParseWarningsBanner warnings={uploadWarnings} />

      <Card>
        {isLoading && <Note>Загрузка…</Note>}
        {isError && <Note>Не удалось загрузить список проверок.</Note>}
        {!isLoading && !isError && items.length === 0 && (
          <Note>Загрузок пока нет. Выберите xlsx или XML ГГЭ выше.</Note>
        )}
        {items.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>Файл</th>
                <th>Статус</th>
                <th>ЛСР</th>
                <th>Позиций</th>
                <th>Загружен</th>
                <th>Разбор</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/agents/smeta/rate-checks/${c.id}`}>{c.source_filename}</Link>
                    {c.error_message && (
                      <div>
                        <Muted>{c.error_message.slice(0, 80)}</Muted>
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge $tone={badgeToneFor(c.status)}>{c.status}</Badge>
                  </td>
                  <td>{c.sheet_count}</td>
                  <td>{c.position_count}</td>
                  <td>
                    <Muted>{fmtDate(c.created_at)}</Muted>
                  </td>
                  <td>
                    <Muted>{fmtDate(c.parsed_at)}</Muted>
                  </td>
                  <td>
                    <Actions>
                      <Button
                        type="button"
                        $variant="ghost"
                        onClick={() => onDelete(c.id, c.source_filename)}
                        disabled={!can_write || (!!c.user_id && !!currentUserId && c.user_id !== currentUserId)}
                        title={
                          !can_write
                            ? 'Только чтение'
                            : c.user_id && currentUserId && c.user_id !== currentUserId
                              ? 'Удалить загрузку может только её автор'
                              : undefined
                        }
                      >
                        Удалить
                      </Button>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Wrap>
  );
}
